import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { List } from '~/Partials/List/List';
import type { StrictOmit, WithId } from '~/Partials/Utils/typeUtils';
import type { Theme } from '~/Utils/Theme';
import type { PortfolioCallToActionProps } from './PortfolioCallToAction/PortfolioCallToAction';
import { PortfolioCallToAction } from './PortfolioCallToAction/PortfolioCallToAction';
import type { PortfolioItemDetails, PortfolioItemProps } from './PortfolioItem/PortfolioItem';
import { PortfolioItem } from './PortfolioItem/PortfolioItem';
import itemStyles from './StudyPortfolioSection.items.module.css';
import styles from './StudyPortfolioSection.module.css';

export type PortfolioItemDetailsWithId = WithId<PortfolioItemDetails>;

export interface StudyPortfolioSectionProps {
    id: string;
    items: PortfolioItemDetailsWithId[];
    callToAction: StrictOmit<PortfolioCallToActionProps, 'theme'>;
    title?: HtmlString;
    teaser?: HtmlString;
    theme?: Theme;
}

const allowedNumberOfItems = 6;

const getIndexBasedProps = (index: number): Pick<PortfolioItemProps, 'background' | 'ratio'> => {
    switch (index) {
        case 0:
            return {
                ratio: '16x9',
                background: 'red',
            };
        case 1:
            return {
                ratio: '1x1',
                background: 'yellow',
            };
        case 2:
            return {
                ratio: '3x2',
                background: 'teal',
            };
        case 3:
            return {
                ratio: '3x4',
                background: 'steelblue',
            };
        case 4:
            return {
                ratio: '2x3',
                background: 'white',
            };
        default:
            return {
                ratio: '16x9',
                background: 'burgundy',
            };
    }
};

export const StudyPortfolioSection: React.FC<StudyPortfolioSectionProps> = props => {
    const contextualTheme = useTheme();

    const { id, title, items, callToAction, teaser, theme = contextualTheme } = props;

    if (items.length !== allowedNumberOfItems) {
        throw new Error(`Portfolio can accept exactly ${allowedNumberOfItems} items`);
    }

    const ctaItem = {
        id: uniqueId('portfolioCTA_'),
        type: 'CTA',
        ...callToAction,
    } as const;

    const extendedItems = [...items, ctaItem];

    return (
        <PageSection id={id} className={styles.StudyPortfolioSection}>
            <div className={styles.Information}>
                {title && <PageSectionHeading heading={title} className={styles.Title} />}
                {teaser && (
                    <div className={styles.TeaserWrapper}>
                        <HtmlDiv
                            html={teaser}
                            className={classNames(styles.Teaser, 'RichtextContent', `RichtextContent--light`)}
                        />
                    </div>
                )}
            </div>
            <List
                className={itemStyles.Items}
                items={extendedItems}
                renderItem={(item, index) => (
                    <li key={item.id} className={itemStyles.Item}>
                        {item.type === 'CTA' ? (
                            <PortfolioCallToAction {...item} theme={theme} />
                        ) : (
                            <PortfolioItem {...getIndexBasedProps(index)} item={item} theme={theme} />
                        )}
                    </li>
                )}
            />
        </PageSection>
    );
};
